/* Clearspot — theme behaviour.
   Everything here is progressive enhancement. If this file fails to load the
   page still reads, and add-to-cart still works, because the buy form is a
   plain POST to /cart/add. */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- reveal
     Sections fade up once as they enter the viewport. Elements start hidden
     only when JS is running (the .js class is set below), so a JS failure
     leaves everything visible rather than blank. */
  function initReveal() {
    var targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    targets.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------ sticky buy
     A slim price + add-to-cart bar that appears once the hero's buy button
     has scrolled out of sight, and hides again when the footer buy block
     comes into view (so you never see two competing CTAs at once). */
  function initStickyBuy() {
    var bar = document.querySelector('[data-sticky-buy]');
    if (!bar) return;

    var sentinels = document.querySelectorAll('[data-buy-sentinel]');
    if (!sentinels.length || !('IntersectionObserver' in window)) return;

    /* Track WHICH sentinels are on screen, not how many.
       A counter looks equivalent and isn't: the observer's first callback
       reports every observed element at once, so the off-screen footer
       sentinel decrements a counter it never incremented. Net zero, bar
       shown — competing with the hero button at scroll position 0. */
    var showing = new Set();

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) showing.add(entry.target);
        else showing.delete(entry.target);
      });
      bar.classList.toggle('is-shown', showing.size === 0);
    }, { threshold: 0 });

    sentinels.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------- quantity */
  function initQuantity() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty]');
      if (!btn) return;

      var wrap = btn.closest('[data-qty-wrap]');
      if (!wrap) return;

      var input = wrap.querySelector('input[name="quantity"], input[type="number"]');
      if (!input) return;

      var step = btn.getAttribute('data-qty') === 'up' ? 1 : -1;
      var min = parseInt(input.getAttribute('min'), 10);
      if (isNaN(min)) min = 1;

      var next = (parseInt(input.value, 10) || min) + step;
      if (next < min) next = min;

      var max = parseInt(input.getAttribute('max'), 10);
      if (!isNaN(max) && next > max) next = max;

      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* --------------------------------------------------------------- gallery
     Thumbnail click swaps the main image. No lightbox — one less thing to
     break on a phone. */
  function initGallery() {
    var gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;

    var main = gallery.querySelector('[data-gallery-main]');
    if (!main) return;

    gallery.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-gallery-thumb]');
      if (!thumb) return;
      e.preventDefault();

      var full = thumb.getAttribute('data-full');
      var alt = thumb.getAttribute('data-alt') || '';
      if (!full) return;

      main.src = full;
      main.alt = alt;
      main.removeAttribute('srcset');

      gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (t) {
        t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
      });
    });
  }

  /* ------------------------------------------------------------ mobile nav */
  function initNav() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var nav = document.querySelector('[data-nav]');
    if (!toggle || !nav) return;

    toggle.addEventListener('click', function () {
      var open = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.classList.toggle('nav-open', open);
    });

    nav.addEventListener('click', function (e) {
      if (!e.target.closest('a')) return;
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('nav-open');
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !nav.classList.contains('is-open')) return;
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('nav-open');
      toggle.focus();
    });
  }

  /* --------------------------------------------------------------- accordion
     The FAQ uses native <details>, which needs no JS. This only adds the
     one-open-at-a-time behaviour, and only where it's asked for. */
  function initAccordion() {
    document.querySelectorAll('[data-accordion]').forEach(function (group) {
      var items = group.querySelectorAll('details');
      items.forEach(function (item) {
        item.addEventListener('toggle', function () {
          if (!item.open) return;
          items.forEach(function (other) {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  /* --------------------------------------------------------- variant price
     Pure display. The variant id is already correct in the form whether or not
     this runs, so a JS failure costs you an updating price label, never a
     wrong order. */
  function initVariantPrice() {
    var target = document.querySelector('[data-price-target]');
    if (!target) return;

    document.addEventListener('change', function (e) {
      var input = e.target.closest('[data-variant-price]');
      if (!input || !input.checked) return;
      target.textContent = input.getAttribute('data-variant-price');
    });
  }

  /* ------------------------------------------------------------- AddToCart
     Fires the Meta pixel event as the buy form submits. Guarded on fbq so the
     page works with no pixel, an ad-blocked pixel, or a failed script load.

     Note this covers the storefront only. Purchase has to be fired from
     Shopify's Customer Events sandbox — see content/meta-pixel-purchase.md. */
  function initPixelEvents() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.getAttribute('action') !== '/cart/add') return;
      if (typeof window.fbq !== 'function') return;

      var idField = form.querySelector('[name="id"]:checked') || form.querySelector('[name="id"]');
      var qtyField = form.querySelector('[name="quantity"]');

      try {
        window.fbq('track', 'AddToCart', {
          content_ids: idField ? [String(idField.value)] : [],
          content_type: 'product',
          num_items: qtyField ? parseInt(qtyField.value, 10) || 1 : 1
        });
      } catch (err) {
        /* Never let analytics block a sale. */
      }
    });
  }

  /* ------------------------------------------------------------------ init */
  document.documentElement.classList.add('js');

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    initReveal();
    initStickyBuy();
    initQuantity();
    initGallery();
    initNav();
    initAccordion();
    initVariantPrice();
    initPixelEvents();
  });
})();
